import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import ProtectedImage from '../components/ProtectedImage';

const Management = () => {
  const { t } = useTranslation();
  const [selectedMember, setSelectedMember] = useState(null);

  // Board of Commissioners
  const commissioners = [
    {
      id: 'jonaydisepriyanto',
      category: 'boardOfCommissioners'
    }
  ];

  // Board of Directors
  const directors = [
    {
      id: 'agusfirmansyah',
      category: 'boardOfDirectors'
    }
  ];

  // Senior Management
  const seniorManagement = [
    {
      id: 'rohollahbehestani',
      category: 'seniorManagement',
      division: 'Bank Instrument Consulting Division'
    },
    {
      id: 'rahmatpamungkas',
      category: 'seniorManagement',
      division: 'Information Technology, Fintech, Crypto & Blockchain Division'
    },
    {
      id: 'afitzun',
      category: 'seniorManagement',
      division: 'Finance & Treasury Division'
    },
    // {
    //   id: 'ibdharma',
    //   category: 'seniorManagement',
    //   division: 'Marketing & Client Relation Division'
    // },
    {
      id: 'aanghanafi',
      category: 'seniorManagement',
      division: 'Human Resources & General Affairs Division'
    },
    {
      id: 'aguspriono',
      category: 'seniorManagement',
      division: 'Legal, Risk & Compliance Division'
    },
    {
      id: 'sammydavid',
      category: 'seniorManagement'
    },
    {
      id: 'stanleyju',
      category: 'seniorManagement'
    },
    {
      id: 'agusmanto',
      category: 'seniorManagement'
    },
    {
      id: 'adamsetiawan',
      category: 'seniorManagement'
    }
  ];

  const openModal = (member) => {
    setSelectedMember(member);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedMember(null);
    document.body.style.overflow = 'unset';
  };

  const renderMemberCard = (member) => {
    const memberData = t(`management.members.${member.id}`, { returnObjects: true });
    
    return (
      <div
        key={member.id}
        className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group"
      >
        {/* Photo */}
        <div className="relative h-80 overflow-hidden bg-gray-100">
          <ProtectedImage
            src={memberData.photo}
            alt={memberData.name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
          />
          {/* Invisible overlay to prevent copy */}
          <div className="absolute inset-0 bg-transparent" onContextMenu={(e) => e.preventDefault()}></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none"></div>
        </div>

        {/* Info */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{memberData.name}</h3>
          <p className="text-maroon-700 font-semibold mb-4 text-sm">{memberData.position}</p>
          
          {member.division && (
            <p className="text-gray-600 text-sm mb-4 italic">{member.division}</p>
          )}

          {/* View More Button */}
          <button
            onClick={() => openModal(member)}
            className="w-full bg-gradient-to-r from-maroon-700 to-maroon-900 hover:from-maroon-800 hover:to-black-900 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 shadow-md hover:shadow-xl"
          >
            {t('management.viewMore')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-maroon-900 to-maroon-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              {t('management.hero.title')}
            </h1>
            <p className="text-xl text-gray-100">
              {t('management.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Board of Commissioners */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              {t('management.boardOfCommissioners')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {commissioners.map(member => renderMemberCard(member))}
          </div>
        </div>
      </section>

      {/* Board of Directors */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              {t('management.boardOfDirectors')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {directors.map(member => renderMemberCard(member))}
          </div>
        </div>
      </section>

      {/* Senior Management */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              {t('management.seniorManagement')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {seniorManagement.map(member => renderMemberCard(member))}
          </div>
        </div>
      </section>

      {/* Modal for Member Details */}
      {selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-maroon-900 to-maroon-700 text-white p-6 flex justify-between items-start">
              <div className="flex-1">
                <h2 className="text-3xl font-bold mb-2">
                  {t(`management.members.${selectedMember.id}.name`)}
                </h2>
                <p className="text-lg text-gray-100">
                  {t(`management.members.${selectedMember.id}.position`)}
                </p>
                {selectedMember.division && (
                  <p className="text-sm text-gray-200 mt-2 italic">{selectedMember.division}</p>
                )}
              </div>
              <button
                onClick={closeModal}
                className="text-white hover:text-gray-200 transition-colors duration-200 ml-4"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-150px)]">
              {/* Content */}
              <div className="space-y-4 text-gray-700 leading-relaxed">
                {(() => {
                  const intro = t(`management.members.${selectedMember.id}.intro`, { defaultValue: '' });
                  return intro && !intro.includes('management.members.') && (
                    <p className="text-lg font-semibold text-gray-900">{intro}</p>
                  );
                })()}

                {(() => {
                  const description = t(`management.members.${selectedMember.id}.description`, { defaultValue: '' });
                  return description && !description.includes('management.members.') && (
                    <p>{description}</p>
                  );
                })()}

                {(() => {
                  const role = t(`management.members.${selectedMember.id}.role`, { defaultValue: '' });
                  return role && !role.includes('management.members.') && (
                    <p className="bg-gray-50 p-4 rounded-lg">{role}</p>
                  );
                })()}

                {(() => {
                  const focus = t(`management.members.${selectedMember.id}.focus`, { defaultValue: '' });
                  return focus && !focus.includes('management.members.') && (
                    <p>{focus}</p>
                  );
                })()}

                {(() => {
                  const background = t(`management.members.${selectedMember.id}.background`, { defaultValue: '' });
                  return background && !background.includes('management.members.') && (
                    <p>{background}</p>
                  );
                })()}

                {(() => {
                  const experience = t(`management.members.${selectedMember.id}.experience`, { defaultValue: '' });
                  return experience && !experience.includes('management.members.') && (
                    <p className="bg-gold-50 p-4 rounded-lg border-l-4 border-gold-600">{experience}</p>
                  );
                })()}

                {(() => {
                  const achievements = t(`management.members.${selectedMember.id}.achievements`, { defaultValue: '' });
                  return achievements && !achievements.includes('management.members.') && (
                    <p>{achievements}</p>
                  );
                })()}

                {(() => {
                  const responsibilities = t(`management.members.${selectedMember.id}.responsibilities`, { defaultValue: '' });
                  return responsibilities && !responsibilities.includes('management.members.') && (
                    <p className="bg-blue-50 p-4 rounded-lg">{responsibilities}</p>
                  );
                })()}

                {(() => {
                  const additional = t(`management.members.${selectedMember.id}.additional`, { defaultValue: '' });
                  return additional && !additional.includes('management.members.') && (
                    <p>{additional}</p>
                  );
                })()}

                {(() => {
                  const expertise = t(`management.members.${selectedMember.id}.expertise`, { defaultValue: '' });
                  return expertise && !expertise.includes('management.members.') && (
                    <p>{expertise}</p>
                  );
                })()}

                {(() => {
                  const expertise1 = t(`management.members.${selectedMember.id}.expertise1`, { defaultValue: '' });
                  return expertise1 && !expertise1.includes('management.members.') && (
                    <p className="pl-4 border-l-2 border-maroon-500">{expertise1}</p>
                  );
                })()}

                {(() => {
                  const expertise2 = t(`management.members.${selectedMember.id}.expertise2`, { defaultValue: '' });
                  return expertise2 && !expertise2.includes('management.members.') && (
                    <p className="pl-4 border-l-2 border-maroon-500">{expertise2}</p>
                  );
                })()}

                {(() => {
                  const expertise3 = t(`management.members.${selectedMember.id}.expertise3`, { defaultValue: '' });
                  return expertise3 && !expertise3.includes('management.members.') && (
                    <p className="pl-4 border-l-2 border-maroon-500">{expertise3}</p>
                  );
                })()}

                {(() => {
                  const expertise4 = t(`management.members.${selectedMember.id}.expertise4`, { defaultValue: '' });
                  return expertise4 && !expertise4.includes('management.members.') && (
                    <p className="pl-4 border-l-2 border-maroon-500">{expertise4}</p>
                  );
                })()}

                {(() => {
                  const expertise5 = t(`management.members.${selectedMember.id}.expertise5`, { defaultValue: '' });
                  return expertise5 && !expertise5.includes('management.members.') && (
                    <p className="pl-4 border-l-2 border-maroon-500">{expertise5}</p>
                  );
                })()}

                {(() => {
                  const approach = t(`management.members.${selectedMember.id}.approach`, { defaultValue: '' });
                  return approach && !approach.includes('management.members.') && (
                    <p>{approach}</p>
                  );
                })()}

                {(() => {
                  const philosophy = t(`management.members.${selectedMember.id}.philosophy`, { defaultValue: '' });
                  return philosophy && !philosophy.includes('management.members.') && (
                    <div className="border-t border-gray-200 pt-4 mt-6">
                      <p className="text-sm italic text-maroon-800 font-semibold">{philosophy}</p>
                    </div>
                  );
                })()}

                {(() => {
                  const commitment = t(`management.members.${selectedMember.id}.commitment`, { defaultValue: '' });
                  return commitment && !commitment.includes('management.members.') && (
                    <div className="border-t border-gray-200 pt-4 mt-6">
                      <p className="text-sm italic text-maroon-800 font-semibold">{commitment}</p>
                    </div>
                  );
                })()}
              </div>

              {/* Close Button */}
              <div className="mt-8 text-center">
                <button
                  onClick={closeModal}
                  className="bg-gradient-to-r from-maroon-700 to-maroon-900 hover:from-maroon-800 hover:to-black-900 text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {t('management.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Management;
